import { useCallback, useEffect, useRef, useState } from "react";

import { useNavigate } from "react-router-dom";

import ConfirmModal from "../components/ConfirmModal/ConfirmModal";
import LoadingEvent from "../components/Dashboard/LoadingEvent";
import NotEvents from "../components/Dashboard/NotEvents";
import NotPhotos from "../components/Dashboard/NotPhotos";
import DashboardControls from "../components/DashboardControls/DashboardControls";
import DashboardHeaderInfo from "../components/DashboardHeaderInfo/DashboardHeaderInfo";
import DashboardPhotos from "../components/DashboardPhotos/DashboardPhotos";
import DashboardQR from "../components/DashboardQR/DashboardQR";
import DashboardSidebar from "../components/DashboardSidebar/DashboardSidebar";
import EditEventModal from "../components/EditEventModal/EditEventModal";
import Header from "../components/Header/Header";
import PhotoSlider from "../components/PhotoSlider/PhotoSlider";
import PhotoUpload from "../components/PhotoUpload/PhotoUpload";
import PremiumExpirationAlert from "../components/PremiumExpirationAlert/PremiumExpirationAlert";
import PricingModal from "../components/PricingModal/PricingModal";
import ToastContainer from "../components/Toast/ToastContainer";
import { useLanguage } from "../contexts/LanguageContext";
import { useUser } from "../contexts/UserContext";
import useToast from "../hooks/useToast";
import apiService from "../services/apiService";
import {
  handleDeleteSelected,
  handleDownloadSelected,
  handlePhotoSelect,
  handleQRDownload,
  handleSelectAll,
  isEventActive,
} from "../utils/dashboardUtils";

import "./EventDashboard.css";

const EventsDashboard = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const {
    user,
    userEvents,
    eventsLoading,
    deleteEvent,
    isLoading,
    updateEvent,
  } = useUser();
  const [selectedEventId, setSelectedEventId] = useState(() => {
    return localStorage.getItem("selectedEventId") || null;
  });
  const [event, setEvent] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [photoSlider, setPhotoSlider] = useState({
    isOpen: false,
    initialIndex: 0,
  });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [aiEnhancing, setAiEnhancing] = useState(false);
  const aiAbortRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPhotos, setTotalPhotos] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  // Eliminado photosLoading, solo usamos loading global
  const PHOTOS_PER_PAGE = 30;

  // Toast hook
  const { toasts, showSuccess, showError, removeToast, showWarning, showInfo } =
    useToast();

  // Estados para modales de confirmación
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: "danger",
    title: "",
    message: "",
    onConfirm: null,
  });

  // Ref para evitar redirecciones múltiples después de eliminación
  const isDeleting = useRef(false);

  // Función helper para mostrar modal de confirmación
  const showConfirmModal = (title, message, onConfirm, type = "danger") => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      onConfirm,
      type,
    });
  };

  const closeConfirmModal = () => {
    setConfirmModal({
      isOpen: false,
      type: "danger",
      title: "",
      message: "",
      onConfirm: null,
    });
  };

  // Clean up selectedEventId when component unmounts (user leaves the page)
  useEffect(() => {
    return () => {
      const currentPath = window?.location?.pathname;
      if (!currentPath?.includes("/events/dashboard")) {
        localStorage.removeItem("selectedEventId");
      }
    };
  }, []);

  useEffect(() => {
    if (userEvents?.length > 0) {
      const storedEventId = localStorage.getItem("selectedEventId");
      if (storedEventId && userEvents.find((e) => e.id === storedEventId)) {
        if (selectedEventId !== storedEventId) {
          setSelectedEventId(storedEventId);
        }
      } else if (!selectedEventId) {
        const firstEventId = userEvents[0].id;
        setSelectedEventId(firstEventId);
        localStorage.setItem("selectedEventId", firstEventId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEvents]);

  // Save selectedEventId to localStorage when it changes
  useEffect(() => {
    if (selectedEventId) {
      localStorage.setItem("selectedEventId", selectedEventId);
    }
  }, [selectedEventId]);

  // Load photos for specific page
  const loadPhotosPage = useCallback(
    async (page, eventId = selectedEventId) => {
      if (!eventId) return;
      try {
        const response = await apiService.getEventPhotos(
          eventId,
          page - 1,
          PHOTOS_PER_PAGE
        );
        if (response.success) {
          setPhotos(response.photos);
          setTotalPhotos(response.pagination.total);
          setTotalPages(Math.ceil(response.pagination.total / PHOTOS_PER_PAGE));
          setCurrentPage(page);
          return response.photos;
        } else {
          console.error("Error loading photos:", response.message);
          setPhotos([]);
          return [];
        }
      } catch (error) {
        console.error("Error loading photos:", error);
        setPhotos([]);
        return [];
      }
    },
    [selectedEventId]
  );

  // Cargar datos del evento seleccionado
  // Optimización: solo ejecutar la carga inicial cuando selectedEventId o userEvents cambian realmente
  const initialLoad = useRef(true);
  useEffect(() => {
    // Evitar múltiples ejecuciones en el primer render
    if (initialLoad.current) {
      initialLoad.current = false;
    } else if (isLoading) {
      return;
    }

    let cancelled = false;
    const loadEventData = async () => {
      if (!selectedEventId) {
        setLoading(false);
        return;
      }
      if (isDeleting.current) {
        return;
      }
      // setLoading(true);
      try {
        const currentEvent = userEvents.find((e) => e.id === selectedEventId);
        if (currentEvent) {
          setEvent(currentEvent);
          setCurrentPage(1);
          setTotalPhotos(0);
          setTotalPages(0);
          await loadPhotosPage(1, selectedEventId);
        } else {
          setSelectedEventId(null);
          setEvent(null);
          setPhotos([]);
          setCurrentPage(1);
          setTotalPhotos(0);
          setTotalPages(0);
        }
      } catch (error) {
        console.error("Error loading event data:", error);
        setPhotos([]);
      }
      if (!cancelled) setLoading(false);
    };
    loadEventData();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEventId, userEvents]);

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      loadPhotosPage(page);
      // Scroll to top of photos container
      const photosContainer = document.querySelector(".photos-container");
      if (photosContainer) {
        photosContainer.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const handleDeletePhoto = (photoId) => {
    showConfirmModal(
      t("dashboard.modalDeletePhoto.deletePhoto"),
      t("dashboard.modalDeletePhoto.deletePhotoSubtitle"),
      async () => {
        try {
          // Call the real API to delete the photo
          await apiService.deleteEventPhoto(photoId, selectedEventId);

          // Instead of updating local state, reload the current page to get updated data
          const remainingPhotosOnPage = photos.length - 1;

          if (remainingPhotosOnPage === 0 && currentPage > 1) {
            await loadPhotosPage(currentPage - 1, selectedEventId);
          } else {
            await loadPhotosPage(currentPage, selectedEventId);
          }

          // Clear the deleted photo from selected photos
          setSelectedPhotos((prev) => {
            const newSet = new Set(prev);
            newSet.delete(photoId);
            return newSet;
          });

          showSuccess(t("dashboard.modalDeletePhoto.showSuccessPhoto"));
        } catch (error) {
          showError(t("dashboard.photoDeleteError.title"));
        }
      }
    );
  };

  const handlePhotoUploaded = async (result) => {
    try {
      // Si es un upload en lote, esperar un poco más antes de recargar
      if (result?.batch) {
        await new Promise((resolve) => setTimeout(resolve, 800));
        await loadPhotosPage(currentPage, selectedEventId);
        const message =
          result.count > 1
            ? t("dashboard.photosUpload.title")
            : t("dashboard.photoUpload.title");
        showSuccess(`${result.count} ${message}`);
      } else {
        // Upload individual
        await loadPhotosPage(currentPage, selectedEventId);
        showSuccess(t("dashboard.photoUpload.title"));
      }
    } catch (error) {
      // Fallback solo para uploads individuales
      if (!result?.batch && currentPage === 1) {
        setPhotos((prevPhotos) => [result, ...prevPhotos]);
      }
      setTotalPhotos((prev) => prev + 1);
      setTotalPages(Math.ceil((totalPhotos + 1) / PHOTOS_PER_PAGE));
      showSuccess(t("dashboard.photoUpload.title"));
    }
  };

  const handleUploadError = (error) => {
    showError(error);
  };

  const handlePhotoClick = (photo, index) => {
    setPhotoSlider({
      isOpen: true,
      initialIndex: index,
    });
  };

  const handleCloseSlider = () => {
    setPhotoSlider({
      isOpen: false,
      initialIndex: 0,
    });
  };

  const handlePhotoView = (photo) => {
    // Navigate to photo editor instead of opening modal
    navigate(`/events/${selectedEventId}/photo/${photo.id}/edit`);
  };

  const handleSliderEdit = (photo) => {
    handleCloseSlider();
    navigate(`/events/${selectedEventId}/photo/${photo.id}/edit`);
  };

  const handleSliderDownload = (photo) => {
    handleDownloadSelected({
      selectedPhotos: new Set([photo.id]),
      photos: [photo],
      user,
      eventName: event?.name,
    });
  };

  const handleSliderDelete = (photoId) => {
    handleCloseSlider();
    handleDeletePhoto(photoId);
  };

  const [enhancementTypeState, setEnhancementType] = useState("professional");
  const handleSliderEnhanceWithAI = async (
    photo,
    enhancementType = "professional",
    prompt
  ) => {
    setEnhancementType(enhancementType);
    setAiEnhancing(true);
    if (enhancementType === "custom") {
      setTimeout(() => setAiEnhancing(false), 5000);
    }
    try {
      if (aiAbortRef.current) {
        try {
          aiAbortRef.current.abort();
        } catch (e) {}
      }
      const controller = new AbortController();
      aiAbortRef.current = controller;

      const result = await apiService.enhancePhotoWithAI(
        photo.id,
        selectedEventId,
        enhancementType,
        prompt,
        controller.signal
      );

      // Para custom, el fetch es fire-and-forget y esta promesa resuelve casi
      // al instante: si ocultamos el overlay acá, desaparece antes de que el
      // usuario llegue a verlo. Dejamos que el setTimeout de arriba lo oculte
      // pasados los 5s, dándole feedback visual instantáneo y sostenido.
      if (enhancementType !== "custom") {
        setAiEnhancing(false);
      }

      // Polling: recarga la galería cada 5s hasta que aparezca una nueva foto.
      // Los prompts personalizados se procesan en background y tardan más, así que
      // les damos una ventana de espera más larga antes de avisar que siga esperando.
      const POLL_INTERVAL = 5000;
      const MAX_POLLS = enhancementType === "custom" ? 24 : 10;
      let polls = 0;
      let foundNew = false;
      const prevPhotoIds = new Set(photos.map((p) => p.id));

      const pollForNewPhoto = async () => {
        polls++;
        const updatedPhotos = await loadPhotosPage(
          currentPage,
          selectedEventId
        );
        const updatedIds = new Set(updatedPhotos.map((p) => p.id));
        for (const id of updatedIds) {
          if (!prevPhotoIds.has(id)) {
            foundNew = true;
            showSuccess(t("photoEnhancement.newPhotoReady"), 6000);
            break;
          }
        }
        if (!foundNew && polls < MAX_POLLS) {
          setTimeout(pollForNewPhoto, POLL_INTERVAL);
        } else if (!foundNew) {
          showWarning(
            t(
              enhancementType === "custom"
                ? "photoEnhancement.pollTimeoutCustom"
                : "photoEnhancement.pollTimeout"
            ),
            8000
          );
        }
      };
      pollForNewPhoto();

      if (result.success) {
        // Show special toast for custom prompts processed in background
        if (result.applied_custom_mode) {
          showInfo(t("photoEnhancement.customBackground"), 9000);
        } else {
          showSuccess(t("photoEnhancement.done"), 4000);
        }
      }
    } catch (error) {
      setAiEnhancing(false);
      if (error?.name === "AbortError") {
        showWarning(t("photoEnhancement.canceled"), 4000);
      } else {
        showError(t("commons.error"), 6000);
      }
    } finally {
      aiAbortRef.current = null;
    }
  };

  const openEditModal = () => setEditModalOpen(true);
  const closeEditModal = () => setEditModalOpen(false);
  const handleSaveEvent = async (payload) => {
    const updated = await updateEvent({
      id: event.id,
      name: payload.name,
      description: payload.description,
      date: payload.date,
      time: payload.time,
      location: payload.location,
    });
    setEvent((prev) => ({ ...prev, ...updated }));
  };

  const handleDeleteEvent = async (eventIdToDelete) => {
    showConfirmModal(
      t("dashboard.modalDeleteEvent.deleteEvent"),
      t("dashboard.modalDeleteEvent.deleteEventSubtitle"),
      async () => {
        try {
          // Marcar que estamos eliminando para evitar redirecciones del useEffect
          isDeleting.current = true;

          // Calcular eventos restantes ANTES de eliminar
          const remainingEvents = userEvents.filter(
            (e) => e.id !== eventIdToDelete
          );

          // Eliminar el evento
          await deleteEvent(eventIdToDelete);

          // Si se elimina el evento seleccionado, seleccionar otro
          if (eventIdToDelete === selectedEventId) {
            if (remainingEvents.length > 0) {
              const newSelectedId = remainingEvents[0].id;
              setSelectedEventId(newSelectedId);
              localStorage.setItem("selectedEventId", newSelectedId);
            } else {
              setSelectedEventId(null);
              setEvent(null);
              setPhotos([]);
              localStorage.removeItem("selectedEventId");
            }
          }

          isDeleting.current = false;
        } catch (error) {
          isDeleting.current = false;
        }
      }
    );
  };

  // Function to copy text to clipboard
  const copyToClipboard = async (text, successMessage) => {
    try {
      await navigator.clipboard.writeText(text);
      showSuccess(successMessage);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  // Copy share URL
  const copyShareUrl = (event) => {
    if (event?.shareUrl) {
      copyToClipboard(event.shareUrl, t("dashboard.shareUrl.copySuccess"));
    }
  };

  const handleDownloadSelectedPhotos = async () => {
    try {
      const selectedPhotoObjects = photos.filter((photo) =>
        selectedPhotos.has(photo.id)
      );

      // Check if user is trying to download multiple photos without premium
      if (selectedPhotoObjects.length > 1 && !user?.isPremium) {
        showSuccess(
          `Downloading ${selectedPhotoObjects.length} photos individually...`
        );
      }

      await handleDownloadSelected({
        selectedPhotos,
        photos,
        user,
        eventName: event?.name || "Selected Photos",
      });

      if (selectedPhotoObjects.length === 1) {
        showSuccess("Photo downloaded successfully!");
      } else if (user?.isPremium) {
        showSuccess(
          `Downloaded ${selectedPhotoObjects.length} photos as ZIP file!`
        );
      } else {
        showSuccess(
          `Downloaded ${selectedPhotoObjects.length} photos individually!`
        );
      }
    } catch (error) {
      showError(error.message || "Failed to download photos");
    }
  };

  const handleDownloadAllPhotos = async () => {
    try {
      if (!user?.isPremium) {
        return;
      }

      showSuccess(
        "Preparing to download all photos. This may take a few moments..."
      );

      // Fetch all photos from the event
      const allPhotos = [];
      const totalPages = Math.ceil(totalPhotos / PHOTOS_PER_PAGE);

      for (let page = 1; page <= totalPages; page++) {
        const response = await apiService.getEventPhotos(
          selectedEventId,
          page - 1,
          PHOTOS_PER_PAGE
        );

        if (response.success) {
          allPhotos.push(...response.photos);
        }
      }

      if (allPhotos.length === 0) {
        showError("No photos found to download");
        return;
      }

      // Create a Set with all photo IDs
      const allPhotoIds = new Set(allPhotos.map((photo) => photo.id));

      await handleDownloadSelected({
        selectedPhotos: allPhotoIds,
        photos: allPhotos,
        user,
        eventName: event?.name || "All Event Photos",
      });

      showSuccess(`Downloaded all ${allPhotos.length} photos as ZIP file!`);
    } catch (error) {
      console.error("Error downloading all photos:", error);
      showError(error.message || "Failed to download all photos");
    }
  };

  // Si no hay eventos
  if (userEvents.length === 0 && !eventsLoading) {
    return <NotEvents />;
  }

  // Mostrar loading solo si está cargando eventos o si está cargando el evento específico y tenemos un selectedEventId
  if (eventsLoading) {
    return <LoadingEvent message={t("commons.loadingEvents")} />;
  }

  return (
    <>
      <title>{t("pagesTitle.dashboard")}</title>
      <div className="event-dashboard-page" key={selectedEventId}>
        <Header />
        <div className="dashboard-layout">
          <DashboardSidebar
            user={user}
            userEvents={userEvents}
            sidebarCollapsed={sidebarCollapsed}
            setSidebarCollapsed={setSidebarCollapsed}
            setSelectedEventId={setSelectedEventId}
            handleDeleteEvent={handleDeleteEvent}
            selectedEventId={selectedEventId}
            setIsPricingModalOpen={setIsPricingModalOpen}
          />

          <div className="dashboard-main">
            <PremiumExpirationAlert
              setIsPricingModalOpen={setIsPricingModalOpen}
            />
            <div className="dashboard-header">
              <DashboardHeaderInfo
                event={event}
                totalPhotos={totalPhotos}
                user={user}
                handleDeleteEvent={handleDeleteEvent}
                openEditModal={openEditModal}
                handleQRDownload={handleQRDownload}
              />
            </div>

            <div className="container">
              {user?.isPremium && photos?.length > 0 && (
                <DashboardControls
                  photosCount={photos.length}
                  selectedCount={selectedPhotos.size}
                  allSelected={selectedPhotos.size === photos.length}
                  totalPhotos={totalPhotos}
                  user={user}
                  onSelectAll={() =>
                    handleSelectAll({
                      photos,
                      selectedPhotos,
                      setSelectedPhotos,
                    })
                  }
                  onDownloadSelected={handleDownloadSelectedPhotos}
                  onDownloadAll={handleDownloadAllPhotos}
                  onDeleteSelected={() => {
                    handleDeleteSelected({
                      selectedPhotos,
                      photos,
                      setPhotos,
                      setSelectedPhotos,
                      showConfirmModal,
                      showSuccess,
                      showError,
                      selectedEventId,
                      t,
                      loadPhotosPage,
                      currentPage,
                    });
                  }}
                />
              )}

              {photos?.length > 0 && (
                <DashboardPhotos
                  photos={photos}
                  selectedPhotos={selectedPhotos}
                  setSelectedPhotos={setSelectedPhotos}
                  handlePhotoSelect={handlePhotoSelect}
                  handleDownloadSelected={(props) =>
                    handleDownloadSelected({
                      ...props,
                      user,
                      eventName: event?.name,
                    })
                  }
                  handleDeletePhoto={handleDeletePhoto}
                  handlePhotoView={handlePhotoView}
                  handlePhotoClick={handlePhotoClick}
                  user={user}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalPhotos={totalPhotos}
                  onPageChange={handlePageChange}
                  photosPerPage={PHOTOS_PER_PAGE}
                  eventName={event?.name || "Event Photos"}
                  setIsPricingModalOpen={setIsPricingModalOpen}
                />
              )}

              {event && !isEventActive(event) && totalPhotos === 0 && (
                <NotPhotos />
              )}

              {event &&
                isEventActive(event) &&
                (user.isPremium ||
                  (!user.isPremium &&
                    totalPhotos < PHOTOS_PER_PAGE &&
                    isEventActive(event))) && (
                  <PhotoUpload
                    user={user}
                    eventId={selectedEventId}
                    onPhotoUploaded={handlePhotoUploaded}
                    onError={handleUploadError}
                  />
                )}

              {event && isEventActive(event) && (
                <DashboardQR
                  event={event}
                  copyShareUrl={copyShareUrl}
                  handleQRDownload={() =>
                    handleQRDownload(event.qrCode, event.name)
                  }
                />
              )}
            </div>
          </div>
        </div>

        <PhotoSlider
          isPremium={user?.isPremium}
          photos={photos}
          initialIndex={photoSlider.initialIndex}
          isOpen={photoSlider.isOpen}
          onClose={handleCloseSlider}
          onDelete={handleSliderDelete}
          onEdit={handleSliderEdit}
          onDownload={handleSliderDownload}
          onEnhanceWithAI={handleSliderEnhanceWithAI}
          onEnhanceStart={() => {}}
        />

        {aiEnhancing && (
          <div className="ai-enhance-overlay">
            <div className="spinner" />
            <h3>{t("photoEnhancement.processingTitle")}</h3>
            <p>
              {enhancementTypeState === "custom"
                ? t("photoEnhancement.processingSubtitleCustom")
                : t("photoEnhancement.processingSubtitle")}
            </p>
            <button
              className="cancel-btn"
              onClick={() => {
                try {
                  aiAbortRef.current?.abort();
                } catch (e) {}
                setAiEnhancing(false);
              }}
            >
              {t("commons.cancel")}
            </button>
          </div>
        )}

        <ConfirmModal
          isOpen={confirmModal.isOpen}
          type={confirmModal.type}
          title={confirmModal.title}
          message={confirmModal.message}
          onConfirm={confirmModal.onConfirm}
          onClose={closeConfirmModal}
        />

        <EditEventModal
          isOpen={editModalOpen}
          onClose={closeEditModal}
          event={event}
          onSave={handleSaveEvent}
        />

        <ToastContainer toasts={toasts} onRemoveToast={removeToast} />

        <PricingModal
          isOpen={isPricingModalOpen}
          onClose={() => setIsPricingModalOpen(false)}
        />
      </div>
    </>
  );
};

export default EventsDashboard;
